import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useEffect, useRef, useState } from "react";

type RateMode = "cpm" | "revenue";

// Up to 4 decimal places, e.g. a CPM of 1.4375¢/mi.
const RATE_INPUT_PATTERN = /^\d*(\.\d{0,4})?$/;

interface SalesOrderRateFieldsProps {
  milesSold: string;
  /** The sell order's revenue (miles amount) — the value that gets saved. */
  revenue: string;
  onRevenueChange: (revenue: string) => void;
}

function toNonNegativeNumber(value: string): number | null {
  const parsed = Number(value);
  return value.trim() !== "" && Number.isFinite(parsed) && parsed >= 0
    ? parsed
    : null;
}

/** CPM in cents per mile, matching the CPM column on the sell order list. */
function calculateCpm(milesSold: string, revenue: string): number | null {
  const miles = toNonNegativeNumber(milesSold);
  const amount = toNonNegativeNumber(revenue);
  return miles !== null && miles > 0 && amount !== null
    ? (amount * 100) / miles
    : null;
}

function calculateRevenue(milesSold: string, cpm: string): string {
  const miles = toNonNegativeNumber(milesSold);
  const rate = toNonNegativeNumber(cpm);
  return miles !== null && rate !== null
    ? String(Math.round(miles * rate) / 100)
    : "";
}

function formatRate(value: number | null): string {
  return value === null ? "" : String(Number(value.toFixed(4)));
}

/**
 * Lets a sell order be priced either by CPM (¢/mi) or by its revenue, and
 * shows both figures. Revenue is what gets saved, so in CPM mode it is
 * recalculated from the miles sold whenever either one changes.
 */
export function SalesOrderRateFields(
  props: Readonly<SalesOrderRateFieldsProps>,
) {
  const { milesSold, revenue, onRevenueChange } = props;
  const [mode, setMode] = useState<RateMode>("cpm");
  const [rate, setRate] = useState(() =>
    formatRate(calculateCpm(milesSold, revenue)),
  );
  // The inputs the current revenue already reflects. Revenue is only
  // recalculated once miles or the rate move away from these, so opening an
  // existing order never rewrites its revenue through a rounded CPM.
  const baseline = useRef({ milesSold, rate });
  const lastEmittedRevenue = useRef(revenue);

  const emitRevenue = (nextRevenue: string) => {
    lastEmittedRevenue.current = nextRevenue;
    onRevenueChange(nextRevenue);
  };

  // Revenue set from outside (loading an order, a source deal, a form reset)
  // re-seeds the rate box.
  useEffect(() => {
    if (revenue === lastEmittedRevenue.current) {
      return;
    }

    lastEmittedRevenue.current = revenue;
    const nextRate =
      mode === "cpm" ? formatRate(calculateCpm(milesSold, revenue)) : revenue;
    baseline.current = { milesSold, rate: nextRate };
    setRate(nextRate);
  }, [revenue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      mode !== "cpm" ||
      (milesSold === baseline.current.milesSold &&
        rate === baseline.current.rate)
    ) {
      return;
    }

    baseline.current = { milesSold, rate };
    emitRevenue(calculateRevenue(milesSold, rate));
  }, [mode, milesSold, rate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModeChange = (value: string) => {
    if (value !== "cpm" && value !== "revenue") {
      return;
    }

    // Carry the current figure across so switching never changes the revenue.
    const nextRate =
      value === "cpm" ? formatRate(calculateCpm(milesSold, revenue)) : revenue;
    baseline.current = { milesSold, rate: nextRate };
    setMode(value);
    setRate(nextRate);
  };

  const handleRateChange = (value: string) => {
    if (!RATE_INPUT_PATTERN.test(value)) {
      return;
    }

    setRate(value);

    if (mode === "revenue") {
      emitRevenue(value);
    }
  };

  return (
    <>
      <div className="space-y-1">
        <Label>Rate</Label>
        <div className="flex gap-2">
          <ToggleGroup
            type="single"
            variant="outline"
            value={mode}
            onValueChange={handleModeChange}
          >
            <ToggleGroupItem value="cpm" className="px-3">
              CPM
            </ToggleGroupItem>
            <ToggleGroupItem value="revenue" className="px-3">
              Revenue
            </ToggleGroupItem>
          </ToggleGroup>
          <Input
            inputMode="decimal"
            value={rate}
            onChange={(event) => handleRateChange(event.target.value)}
            placeholder={mode === "cpm" ? "1.5" : "1200"}
            aria-label={mode === "cpm" ? "CPM (¢/mi)" : "Revenue"}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label>CPM (¢/mi)</Label>
        <Input value={formatRate(calculateCpm(milesSold, revenue))} disabled />
      </div>
      <div className="space-y-1">
        <Label>Revenue</Label>
        <Input value={revenue} disabled />
      </div>
    </>
  );
}
